<template>
  <div class="order-totals">
    <div
      v-if="!order.orderCoupon"
      class="order-totals__coupon"
    >
      <apply-coupon
        :existing-coupon-code="order?.orderCoupon?.code"
        :order-id="order.id"
        @snackbarMessage="setSnackbarMessage"
        @cartUpdated="$emit('cartUpdated')"
      />
    </div>
    <div
      class="order-totals__summary"
      aria-live="polite"
      aria-atomic="true"
    >
      <div class="order-totals__list">
        <div
          v-if="order.orderCoupon"
          class="order-totals__row order-totals__row--coupon"
        >
          <div class="order-totals__coupon-applied">
            <div class="coupon-chip">
              <svg
                aria-hidden="true"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                viewBox="0 0 24 24"
              >
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line
                  x1="7"
                  y1="7"
                  x2="7.01"
                  y2="7"
                />
              </svg>
              <span class="coupon-chip__code">{{ order.orderCoupon.code }}</span>
              <RemoveCoupon
                :order-id="order.id"
                @snackbarMessage="setSnackbarMessage"
                @cartUpdated="$emit('cartUpdated')"
              />
            </div>
            <div
              v-if="order.orderCoupon.description"
              class="coupon-chip__desc"
            >
              {{ order.orderCoupon.description }}
            </div>
          </div>
        </div>
        <div class="order-totals__row">
          <div class="order-totals__label">
            {{ $t("checkout.subtotal") }}
          </div>
          <div class="order-totals__value">
            <span
              class="ot__price-val"
            >
              <price
                :book-price="Number(order.totalLineItemsPrice)"
                :base-currency="order.currency._id"
                :show-free-text="false"
              />
            </span>
          </div>
        </div>
        <div
          v-if="Number(order.shippingFee) > 0"
          class="order-totals__row"
        >
          <div class="order-totals__label">
            {{ $t("checkout.shipping") }}
          </div>
          <div class="order-totals__value">
            <span
              class="ot__price-val"
            >
              <price
                :book-price="Number(order.shippingFee)"
                :base-currency="order.currency._id"
                :show-free-text="false"
              />
            </span>
          </div>
        </div>
        <div class="order-totals__row">
          <div class="order-totals__label">
            {{ $t("checkout.vat") }}
          </div>
          <div class="order-totals__value">
            <span
              class="ot__price-val"
            >
              <price
                :book-price="Number(order.totalTax)"
                :base-currency="order.currency._id"
                :show-free-text="false"
              />
            </span>
          </div>
        </div>
        <div
          v-if="taxInclusiveDiscount > 0"
          class="order-totals__row order-totals__row--discount"
        >
          <div class="order-totals__label">
            {{ $t("checkout.discount") }}
          </div>
          <div class="order-totals__value">
            <span
              class="ot__price-val"
            >
              <price
                :book-price="taxInclusiveDiscount"
                :base-currency="order.currency._id"
                :show-free-text="false"
              />
            </span>
          </div>
        </div>
        <div class="order-totals__row order-totals__row--total">
          <div class="order-totals__label order-totals__label--total">
            {{ $t("checkout.total") }}
          </div>
          <div class="order-totals__value order-totals__value--total">
            <span
              class="ot__price-val ot__price-val--total"
            >
              <price
                :book-price="displayTotalPrice"
                :base-currency="order.currency._id"
              />
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApplyCoupon from '@/components/coupons/ApplyCoupon';
import Price from '@/components/Price';
import RemoveCoupon from '@/components/coupons/RemoveCoupon.vue';
import { getOrderDiscountWithTax } from '@/utils/priceCalculator';
import { ROUND_TO_INT_CURRENCIES } from '@/utils/Const';

export default {
  name: "OrderTotals",
  components: {
    ApplyCoupon,
    Price,
    RemoveCoupon,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    setSnackbarMessage: {
      type: Function,
      required: true,
    },
  },
  computed: {
    taxInclusiveDiscount() {
      return getOrderDiscountWithTax(this.order);
    },
    displayTotalPrice() {
      const totalPrice = Number(this.order?.totalPrice || 0);
      const currencyCode = this.order?.currency?._id;

      if (ROUND_TO_INT_CURRENCIES.includes(currencyCode)) {
        return Math.round(totalPrice);
      }

      return Number(totalPrice.toFixed(2));
    },
  },
};
</script>

<style scoped lang="scss">
.order-totals {
  padding: 0 16px;
  font-family: $book-font-body;
}

.order-totals__coupon {
  padding: 10px 12px;
  background: $book-grey-lightest;
  border-radius: 8px;
  margin-bottom: 8px;
}

.order-totals__summary {
  background: $book-grey-lightest;
  border-radius: 8px;
  padding: 12px;
}

.order-totals__list {
  width: 100%;
}

.order-totals__row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 3px 0;
}

.order-totals__row--coupon {
  padding-bottom: 8px;
  margin-bottom: 4px;
  border-bottom: 1px dashed $book-grey-light;
}

.order-totals__coupon-applied {
  width: 100%;
}

.coupon-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba($book-green, 0.08);
  border: 1px solid rgba($book-green, 0.2);
  border-radius: 6px;
  padding: 4px 6px 4px 8px;
  color: $book-green;
}

.coupon-chip__code {
  font-family: $book-font-body;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.coupon-chip__desc {
  font-family: $book-font-body;
  font-size: 0.7rem;
  color: $book-grey;
  margin-top: 4px;
  line-height: 1.3;
}

.order-totals__row--discount .order-totals__label,
.order-totals__row--discount .order-totals__value {
  color: $book-green;
}

.order-totals__row--total {
  padding-top: 8px;
  margin-top: 4px;
  border-top: 1px solid $book-grey-lighter;
}

.order-totals__label {
  font-family: $book-font-body;
  font-size: 0.775rem;
  font-weight: 600;
  color: $book-ink;
}

.order-totals__label--total {
  font-family: $book-font-body;
  font-size: 0.9rem;
  font-weight: 600;
  color: $book-ink;
}

.order-totals__value {
  font-family: $book-font-body;
  font-size: 0.775rem;
  font-weight: 700;
  color: $book-ink;
  text-align: right;
}

.order-totals__value--total {
  font-family: $book-font-body;
  font-size: 1.05rem;
  font-weight: 700;
  color: $book-ink;
}


/* Override Price component gradient-text */
::v-deep .gradient-text {
  background: none !important;
  -webkit-background-clip: unset !important;
  -webkit-text-fill-color: inherit !important;
  color: inherit !important;
  font-family: inherit !important;
  font-size: inherit !important;
  font-weight: inherit !important;
}

/* Discount row price override */
::v-deep .order-totals__row--discount .gradient-text {
  -webkit-text-fill-color: $book-green !important;
  color: $book-green !important;
}

/* Original price (strikethrough) when coupon applied */
.ot__price-val--original {
  text-decoration: line-through;
  color: $book-grey;
  font-size: 0.8rem;
  font-weight: 500;
  margin-right: 6px;
}

::v-deep .ot__price-val--original .gradient-text {
  -webkit-text-fill-color: $book-grey !important;
  color: $book-grey !important;
  font-size: 0.8rem !important;
  font-weight: 500 !important;
}

/* Total row price override */
::v-deep .order-totals__value--total .gradient-text {
  -webkit-text-fill-color: $book-ink !important;
  color: $book-ink !important;
  font-family: $book-font-body !important;
  font-size: 1.05rem !important;
  font-weight: 700 !important;
}

</style>
